import React from "react";
import { RadioState as AriaKitRadioState } from "ariakit";

type RadioGroupState = {
  /**
   * The name of the radio group.
   */
  name: string;
  /**
   * The currently selected value of the radio group.
   */
  selected: string | null;
  /**
   * Whether the radio group should be horizontally aligned.
   */
  horizontal: boolean;
  /**
   * Whether the radio group should have a value selected before submitting the form.
   */
  required: boolean;
  /**
   * Whether all radio inputs should be disabled.
   */
  disabled: boolean;
  /**
   * The radio group state from AriaKit
   */
  radio: AriaKitRadioState;

  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onInvalid: React.FormEventHandler<HTMLInputElement>;

  /**
   * Whether the radio group is invalid.
   */
  invalid: boolean;
};

const MISSING_REQUIRED_CONTEXT = Symbol();

const RadioGroupStateContext = React.createContext<RadioGroupState | typeof MISSING_REQUIRED_CONTEXT>(MISSING_REQUIRED_CONTEXT);

RadioGroupStateContext.displayName = "RadioGroupState";

export const useRadioGroupState = (): RadioGroupState => {
  const context = React.useContext(RadioGroupStateContext);

  if (context === MISSING_REQUIRED_CONTEXT) {
    throw new Error("useRadioContext must be used within RadioGroupStateProvider");
  }

  return context;
};

export const RadioGroupStateProvider: React.FC<RadioGroupState> = (props) => {
  return <RadioGroupStateContext.Provider value={props}>{props.children}</RadioGroupStateContext.Provider>;
};

RadioGroupStateProvider.displayName = "RadioGroupStateProvider";
